import React, { useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { Lock, Eye, Share2, Globe2 } from 'lucide-react';

export default function PrivacySettings() {
  const { theme } = useTheme();
  const [settings, setSettings] = useState({
    visibility: {
      profile: 'public' as 'public' | 'followers' | 'private',
      activity: 'followers' as 'public' | 'followers' | 'private',
      email: false,
      location: true
    },
    dataSharing: {
      thirdParty: false,
      recommendations: true
    }
  });

  return (
    <div className="space-y-8">
      {/* Profile Visibility */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Eye className="h-5 w-5 text-blue-500" />
          <h3 className={`text-lg font-medium ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Profile Visibility
          </h3>
        </div>

        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Who can see your profile
            </label>
            <select
              value={settings.visibility.profile}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                visibility: {
                  ...prev.visibility,
                  profile: e.target.value as typeof settings.visibility.profile
                }
              }))}
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            >
              <option value="public">Everyone</option>
              <option value="followers">Followers Only</option>
              <option value="private">Only You</option>
            </select>
          </div>

          <div>
            <label className={`block text-sm font-medium mb-2 ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Activity visibility
            </label>
            <select
              value={settings.visibility.activity}
              onChange={(e) => setSettings(prev => ({
                ...prev,
                visibility: {
                  ...prev.visibility,
                  activity: e.target.value as typeof settings.visibility.activity
                }
              }))}
              className={`
                w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:outline-none
                ${theme === 'dark'
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
                }
              `}
            >
              <option value="public">Everyone</option>
              <option value="followers">Followers Only</option>
              <option value="private">Only You</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Show Email Address
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Display your email on your public profile
              </p>
            </div>
            <button
              onClick={() => setSettings(prev => ({
                ...prev,
                visibility: { ...prev.visibility, email: !prev.visibility.email }
              }))}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full
                transition-colors duration-200 ease-in-out focus:outline-none
                ${settings.visibility.email
                  ? 'bg-blue-600'
                  : theme === 'dark'
                    ? 'bg-gray-700'
                    : 'bg-gray-200'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white shadow
                  transition-transform duration-200 ease-in-out
                  ${settings.visibility.email ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Show Location
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Display your location on your public profile
              </p>
            </div>
            <button
              onClick={() => setSettings(prev => ({
                ...prev,
                visibility: { ...prev.visibility, location: !prev.visibility.location }
              }))}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full
                transition-colors duration-200 ease-in-out focus:outline-none
                ${settings.visibility.location
                  ? 'bg-blue-600'
                  : theme === 'dark'
                    ? 'bg-gray-700'
                    : 'bg-gray-200'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white shadow
                  transition-transform duration-200 ease-in-out
                  ${settings.visibility.location ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Data Sharing */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Share2 className="h-5 w-5 text-green-500" />
          <h3 className={`text-lg font-medium ${
            theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
          }`}>
            Data Sharing
          </h3>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Third-Party Data Sharing
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Allow sharing data with trusted partners
              </p>
            </div>
            <button
              onClick={() => setSettings(prev => ({
                ...prev,
                dataSharing: { ...prev.dataSharing, thirdParty: !prev.dataSharing.thirdParty }
              }))}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full
                transition-colors duration-200 ease-in-out focus:outline-none
                ${settings.dataSharing.thirdParty
                  ? 'bg-blue-600'
                  : theme === 'dark'
                    ? 'bg-gray-700'
                    : 'bg-gray-200'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white shadow
                  transition-transform duration-200 ease-in-out
                  ${settings.dataSharing.thirdParty ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className={`font-medium ${
                theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
              }`}>
                Personalized Recommendations
              </p>
              <p className={`text-sm ${
                theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
              }`}>
                Allow us to analyze your activity for better recommendations
              </p>
            </div>
            <button
              onClick={() => setSettings(prev => ({
                ...prev,
                dataSharing: { ...prev.dataSharing, recommendations: !prev.dataSharing.recommendations }
              }))}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full
                transition-colors duration-200 ease-in-out focus:outline-none
                ${settings.dataSharing.recommendations
                  ? 'bg-blue-600'
                  : theme === 'dark'
                    ? 'bg-gray-700'
                    : 'bg-gray-200'
                }
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white shadow
                  transition-transform duration-200 ease-in-out
                  ${settings.dataSharing.recommendations ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end space-x-4">
        <button
          className={`
            px-4 py-2 rounded-lg transition-colors
            ${theme === 'dark'
              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }
          `}
        >
          Reset to Default
        </button>
        <button
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Save Changes
        </button>
      </div>
    </div>
  );
}